import { computed, inject, Signal, signal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ProfileTheme } from '@rma/public/generic/domain/types';
import { catchError, concatMap, filter, map, of, pipe, tap } from 'rxjs';
import { AgentDetails } from '../domain/agent';
import { HARD_LIMIT } from '../domain/shortlist';
import { AgencyThemeService } from './agency-theme.service';
import { CompareAgentService } from './compare-agent.service';

export interface ShortlistAgents {
  /** Shortlist order. A code still in flight is simply absent. */
  agents: Signal<AgentDetails[]>;
    agencyThemes: Signal<ReadonlyMap<string, ProfileTheme>>;
  agentsLoading: Signal<boolean>;
  agentsFailed: Signal<boolean>;
}

/**
 * Must be called in an injection context. Only ever asks for codes it has not already seen, so
 * adding one agent costs one request and removals cost nothing.
 */
export function createShortlistAgents(agentCodes: Signal<string[]>): ShortlistAgents {
  const compareAgentService = inject(CompareAgentService);
  const agencyThemeService = inject(AgencyThemeService);

  const resolvedAgents = signal<ReadonlyMap<string, AgentDetails>>(new Map());
  const resolvedThemes = signal<ReadonlyMap<string, ProfileTheme>>(new Map());
  const agentsLoading = signal(false);
  const agentsFailed = signal(false);

  const agents = computed(() =>
    agentCodes()
      .map((agentCode) => resolvedAgents().get(agentCode))
      .filter((agent): agent is AgentDetails => agent !== undefined),
  );

  // Derived from the live shortlist, not everything ever resolved, so a removed agent stops costing
  // a lookup and the set cannot grow unbounded across a session.
  const agencyCodesNeeded = computed(() => {
    const unbranded = agents().filter((agent) => !agent.theme?.rectangleLogoImage);

    return [...new Set(unbranded.map((agent) => agent.agencyCode).filter((agencyCode): agencyCode is string => !!agencyCode))];
  });

  // concatMap, not switchMap: each emission asks only for codes not already held, so cancelling an
  // in-flight batch would drop those agents until something unrelated moved the shortlist again.
  const loadAgents = rxMethod<string[]>(
    pipe(
      map((codes) => codes.filter((agentCode) => !resolvedAgents().has(agentCode))),
      filter((missing) => missing.length > 0),
      tap(() => {
        agentsLoading.set(true);
        agentsFailed.set(false);
      }),
      concatMap((missing) =>
        // HARD_LIMIT is the largest set the shortlist allows, and the endpoint defaults to ten —
        // without it a full shortlist would silently come back truncated.
        compareAgentService.agents(missing, 'TotalReviews', HARD_LIMIT).pipe(
          tap(({ results }) => {
            resolvedAgents.update(
              (known) => new Map([...known, ...results.map((agent): [string, AgentDetails] => [agent.agentCode, agent])]),
            );
            agentsLoading.set(false);
          }),
          catchError(() => {
            // The tiles already resolved stay on screen; the next shortlist change retries.
            agentsFailed.set(true);
            agentsLoading.set(false);

            return of(null);
          }),
        ),
      ),
    ),
  );

  // Tracks codes asked for, not codes answered. An agency with no branding at all is a valid answer
  // that yields no map entry, so filtering on the resolved map alone would ask for it again on every
  // shortlist change — a permanent request for a permanently empty result.
  const requestedAgencyCodes = new Set<string>();

  const loadAgencyThemes = rxMethod<string[]>(
    pipe(
      map((codes) => codes.filter((agencyCode) => !requestedAgencyCodes.has(agencyCode))),
      filter((missing) => missing.length > 0),
      tap((missing) => missing.forEach((agencyCode) => requestedAgencyCodes.add(agencyCode))),
      // One request for every outstanding agency. AgencyThemeService swallows its own failures, so
      // there is nothing to catch here.
      concatMap((missing) =>
        agencyThemeService.themes(missing).pipe(tap((themes) => resolvedThemes.update((known) => new Map([...known, ...themes])))),
      ),
    ),
  );

  loadAgents(agentCodes);
  loadAgencyThemes(agencyCodesNeeded);

  return {
    agents,
    agencyThemes: resolvedThemes.asReadonly(),
    agentsLoading: agentsLoading.asReadonly(),
    agentsFailed: agentsFailed.asReadonly(),
  };
}
